import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import PagePermissionAuditLog from './PagePermissionAuditLog';

export default function PagePermissionsTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [changes, setChanges] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch permission matrix
  const { data: matrixData, isLoading, error } = useQuery({
    queryKey: ['pagePermissions', 'matrix'],
    queryFn: async () => {
      const data = await api.pagePermissions.getMatrix();
      return data;
    }
  });

  // Mutation for saving changes
  const saveMutation = useMutation({
    mutationFn: (changesArray) => api.pagePermissions.bulkUpdate(changesArray),
    onSuccess: () => {
      toast({
        title: t('common.success'),
        description: t('settings.pagePermissions.saveSuccess'),
      });
      setChanges({});
      queryClient.invalidateQueries(['pagePermissions']);
      // Also invalidate user's pages cache
      queryClient.invalidateQueries(['user', 'pages']);
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: t('settings.pagePermissions.saveError'),
        variant: 'destructive',
      });
      console.error('Save error:', error);
    }
  });

  const handleCheckboxChange = (pageName, role, value) => {
    const key = `${pageName}_${role}`;

    setChanges(prev => ({
      ...prev,
      [key]: {
        page_name: pageName,
        role: role,
        can_access: value
      }
    }));
  };

  const handleSave = () => {
    if (Object.keys(changes).length === 0) {
      toast({
        title: t('common.info'),
        description: t('settings.pagePermissions.noChanges'),
      });
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirm = () => {
    const changesArray = Object.values(changes);
    saveMutation.mutate(changesArray);
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setChanges({});
    toast({
      title: t('common.info'),
      description: t('settings.pagePermissions.changesCancelled'),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">Error loading page permissions: {error.message}</div>
      </div>
    );
  }

  if (!matrixData || !matrixData.categories || matrixData.categories.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">No page permission data available</div>
      </div>
    );
  }

  const roles = ['TEACHER', 'INTERVIEWER', 'HOUSE_OWNER', 'PARENT', 'OFFICE'];
  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.pagePermissions.title')}</CardTitle>
          <CardDescription>{t('settings.pagePermissions.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Action buttons */}
          {hasChanges && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-4">
              <div className="text-sm text-blue-900 dark:text-blue-200">
                {t('settings.pagePermissions.unsavedChanges', { count: Object.keys(changes).length })}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? t('common.saving') : t('settings.pagePermissions.saveChanges')}
                </Button>
              </div>
            </div>
          )}

          {/* Permission Matrix Table */}
          <div className="overflow-x-auto">
            {matrixData?.categories?.map(category => (
              <div key={category.name} className="mb-8">
                <h3 className="mb-4 text-lg font-semibold text-foreground">
                  {t('settings.pagePermissions.categories.' + category.name)}
                </h3>
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full border-collapse">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="border-b border-r border-border p-3 text-left text-sm font-semibold text-foreground">
                          {t('settings.pagePermissions.pageColumn')}
                        </th>
                        {roles.map(role => (
                          <th key={role} className="border-b border-border p-3 text-center text-sm font-semibold text-foreground">
                            <div>{t('settings.permissions.roles.' + role)}</div>
                            <div className="mt-1 text-xs font-normal text-muted-foreground">
                              {t('settings.pagePermissions.accessLabel')}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-background">
                      {category.pages.map((page, idx) => {
                        const isLastRow = idx === category.pages.length - 1;
                        return (
                          <tr
                            key={page.page_name}
                            className="hover:bg-muted/50 transition-colors"
                          >
                            <td className={`border-r border-border p-3 ${!isLastRow ? 'border-b' : ''}`}>
                              <div className="font-medium text-sm text-foreground">
                                {t('settings.pagePermissions.pages.' + page.page_name)}
                              </div>
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {t('settings.pagePermissions.descriptions.' + page.page_name)}
                              </div>
                            </td>
                            {roles.map(role => {
                              const perm = page.permissions[role] || {};
                              const key = `${page.page_name}_${role}`;
                              const change = changes[key];
                              const canAccess = change ? change.can_access : perm.can_access;
                              const hasPageChange = !!change;

                              return (
                                <td
                                  key={role}
                                  className={`p-3 text-center ${!isLastRow ? 'border-b border-border' : ''} ${hasPageChange ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
                                >
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={canAccess || false}
                                      onCheckedChange={(val) =>
                                        handleCheckboxChange(page.page_name, role, val)
                                      }
                                    />
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Fixed save button at bottom */}
          <div className="mt-6 flex justify-end border-t border-border pt-4">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              size="lg"
            >
              {saveMutation.isPending
                ? t('common.saving')
                : t('settings.pagePermissions.saveChanges')
              }
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <PagePermissionAuditLog />

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-xl border border-border">
            <h3 className="mb-4 text-lg font-semibold text-foreground">
              {t('settings.pagePermissions.confirmTitle')}
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {t('settings.pagePermissions.confirmDescription', { count: Object.keys(changes).length })}
            </p>
            <div className="max-h-64 overflow-y-auto rounded border border-border bg-muted/50 p-4">
              {Object.values(changes).map((change, idx) => (
                <div key={idx} className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {t('settings.permissions.roles.' + change.role)} - {t('settings.pagePermissions.pages.' + change.page_name)}
                  </span>
                  <span className={change.can_access ? 'text-green-600' : 'text-red-600'}>
                    {change.can_access ? t('settings.pagePermissions.granted') : t('settings.pagePermissions.denied')}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleConfirm} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? t('common.saving') : t('common.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
